<script setup lang="ts">
import { backupRepository } from "@/repositories/backup.repository";
import { trackTab } from "@/analytics/drawing-analytics";
import { computed, ref, toRef } from "vue";
import BackupItem from "./BackupItem.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import { useCreateEventLog, useGetBackups } from "@/stores/queries";
import DrawerMenuContent from "./DrawerMenuContent.vue";
import { useSaveDrawing } from "@/stores/SaveDrawingState";
import { useTranslation } from "i18next-vue";

import UserState from "@/stores/UserState";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import { DraggerType } from "@/drawing";
import { reopenDrawing } from "@/utils/reopen-drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { EventType } from "@/types";

const { createEvent } = useCreateEventLog();
const { t } = useTranslation();
const props = defineProps<{
  drawingId: number;
}>();

defineEmits<{
  (e: "close"): void;
}>();

const isSuperAdmin = computed(() => UserState.isSuperAdmin());

const { SaveState } = useSaveDrawing();

const drawingId = toRef(props, "drawingId");

const { data: backups, fetch } = useGetBackups(drawingId);

const creatingBackupStatus = ref<"idle" | "loading" | "done">("idle");

async function createBackup() {
  trackTab("backups", "backup_created");
  // Validate drawingId before attempting backup
  if (!Number.isInteger(drawingId.value) || drawingId.value <= 0) {
    console.error("[BackupsMenu] Invalid drawingId:", drawingId.value);
    alert(t("canvas:settings.backups.section.create.error"));
    return;
  }

  creatingBackupStatus.value = "loading";
  await backupRepository.createFromDrawingId(drawingId.value, {
    onSuccess: () => {
      fetch();
      createEvent(EventType.DRAWING_CREATE_BACKUP);
      creatingBackupStatus.value = "done";
    },
    onError: (error: Error) => {
      console.error("[BackupsMenu] Backup creation failed:", error);
      alert(t("canvas:settings.backups.section.create.error"));
    },
  });
  setTimeout(() => {
    creatingBackupStatus.value = "idle";
  }, 2000);
}

// DEGU FUNCTIONS
const isApplyingDwg = ref(false);
async function applyNewArrayBuffer(dwg: ArrayBuffer) {
  isApplyingDwg.value = true;
  try {
    await localDrawingRepository.saveDraft(props.drawingId, dwg, false);

    // Close current file and reopen with new data
    await reopenDrawing(props.drawingId, dwg);

    DraggerMenuState.setActiveDragger({
      name: DraggerType.None,
    });
  } catch (e: any) {
    console.error("Error applying backup:", e);
    alert("Erreur lors de l'application de la sauvegarde");
  } finally {
    isApplyingDwg.value = false;
  }
}
const importDwgBackup = async () => {
  trackTab("backups", "backup_imported");
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".dwg";

  input.onchange = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      alert("Aucun fichier sélectionné");
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      applyNewArrayBuffer(arrayBuffer);
    } catch (error) {
      console.error("Erreur lors de la lecture du fichier:", error);
      alert("Impossible de lire le fichier");
    }
  };

  input.click();
};
</script>

<template>
  <DrawerMenuContent
    :label="$t('canvas:settings.backups.section.title')"
    :description="$t('canvas:settings.backups.section.description')"
    actions
  >
    <div class=" ">
      <div v-if="backups?.length" class="d-flex flex-column">
        <BackupItem
          v-for="backup in backups"
          :key="backup.id"
          :backup="backup"
          @backup-applied="$emit('close')"
        />
      </div>

      <div v-else class="py-5 text-center text-grey fw-light">
        <small>{{ $t("canvas:settings.backups.section.noitem") }}</small>
      </div>
    </div>

    <template #actions>
      <DRBtn
        :disabled="SaveState !== 'saved'"
        class="w-100 mt-2"
        :loading="creatingBackupStatus === 'loading'"
        :phosphor-icon="creatingBackupStatus === 'done' ? 'check' : 'plus'"
        variant="outline-white"
        :label="$t('canvas:settings.backups.section.create.button')"
        @click="createBackup"
      />
      <DRBtn
        v-if="isSuperAdmin"
        class="w-100 mt-2"
        phosphor-icon="upload-simple"
        variant="superadmin"
        label="Importer une sauvegarde"
        @click="importDwgBackup"
        :loading="isApplyingDwg"
      />
    </template>
  </DrawerMenuContent>
</template>

<style lang="scss" scoped></style>
